import Axios from "./AxiosInterceptorsService";
import {requestService} from "./RequestService";
// import {ChangePasswordRequestDTO} from "../../Data/request/ChangePasswordRequest";
import AuthenticationResponse from "../domain/Authentication/AuthenticationResponse";
import {ApiRoutes} from "../routes/ApiRoutes";
import {RouteUtils} from "../routes/RouteUtils";
import i18n from "i18next";
import rawAxios from "axios";
import {BoLoginFlow} from "./BoLoginFlow";

export const AccountService = {
    async login(credentials: any): Promise<AuthenticationResponse> {
        if (!ApiRoutes.REACT_APP_URL_AUTHENTICATE) {
            throw new Error("REACT_APP_API_URL_BACKEND is not defined");
        }
        return requestService.post(ApiRoutes.REACT_APP_URL_AUTHENTICATE, credentials).then((response) => {
            return response.data as AuthenticationResponse;
        });
    },

    async boLogin(token: string): Promise<AuthenticationResponse> {
        if (!ApiRoutes.REACT_APP_URL_BO_LOGIN) {
            throw new Error("REACT_APP_URL_BO_LOGIN is not defined");
        }
        return requestService.post(ApiRoutes.REACT_APP_URL_BO_LOGIN,
            {token}
        ).then((response) => {
            return response.data as AuthenticationResponse;
        });
    },

    /**
     * Legacy -> React handshake: exchange the one-shot token from the legacy backoffice for our own JWTs.
     *
     * Runs under {@link BoLoginFlow} so it can never execute twice at once (the token is consumed by the
     * first call, a second one would 404 and bounce the user back to the legacy in a loop).
     * Any previous local session is wiped locally first: going through the axios interceptor for a
     * server logout with a stale token used to answer 401 -> failed refresh -> redirect to the legacy
     * in the middle of the login.
     */
    completeBoLogin(token: string): Promise<AuthenticationResponse> {
        return BoLoginFlow.run(async () => {
            AccountService.revokeSessionSilently(AccountService.getRefreshToken());
            AccountService.clearLocalSession();

            const response = await AccountService.boLogin(token);
            AccountService.saveToken(response.accessToken);
            AccountService.saveRefreshToken(response.refreshToken);
            AccountService.saveUser(response);
            RouteUtils.resetReboRedirectGuard();
            return response;
        });
    },

    /** Remove every locally stored credential without calling the server. */
    clearLocalSession() {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user-' + RouteUtils.getContext());
    },

    /**
     * Best effort, fire-and-forget server side revocation of a previous refresh token.
     * Uses a bare axios instance on purpose: it must never trigger the refresh/redirect interceptor.
     */
    revokeSessionSilently(refreshToken: string | null) {
        if (!refreshToken || !ApiRoutes.REACT_APP_URL_LOGOUT) {
            return;
        }
        rawAxios
            .get(ApiRoutes.REACT_APP_URL_LOGOUT, {headers: {Authorization: 'Bearer ' + refreshToken}})
            .catch(() => {
                // expired/unknown token: nothing to revoke
            });
    },

    async googleLogin(idToken: string): Promise<AuthenticationResponse> {
        if (!ApiRoutes.REACT_APP_URL_GOOGLE_LOGIN) {
            throw new Error("REACT_APP_URL_GOOGLE_LOGIN is not defined");
        }
        return requestService.post(ApiRoutes.REACT_APP_URL_GOOGLE_LOGIN,
            {idToken}
        ).then((response) => {
            return response.data as AuthenticationResponse;
        });
    },

    saveToken(accessToken: string) {
        localStorage.setItem('access_token', accessToken);
    },

    saveRefreshToken(refreshToken: string) {
        localStorage.setItem('refresh_token', refreshToken);
    },

    saveLanguageLocal(language: any) {
        localStorage.setItem('language-' + RouteUtils.getContext(), btoa(JSON.stringify(language)));
        i18n.changeLanguage(language.name);
    },

    saveUser(user: AuthenticationResponse) {
        localStorage.setItem('user-' + RouteUtils.getContext(), btoa(JSON.stringify(user)));
    },

    getAccessToken() {
        return localStorage.getItem('access_token');
    },

    getRefreshToken() {
        return localStorage.getItem('refresh_token');
    },

    saveRedirectAfterLogin(location: string) {
        // Save the current location to redirect to after login
        return localStorage.setItem('redirectAfterLogin', location);
    },

    getRedirectAfterLogin() {
        return localStorage.getItem('redirectAfterLogin');
    },

    getUser(): AuthenticationResponse | null {
        const storageUser = localStorage.getItem('user-' + RouteUtils.getContext());
        let decodedStorageUser = null;
        if (storageUser !== null) {
            decodedStorageUser = atob(storageUser);
            decodedStorageUser = JSON.parse(decodedStorageUser);
        }
        return decodedStorageUser as AuthenticationResponse | null;
    },

    getLanguageLocalStorage() {
        const storageLanguage = localStorage.getItem('language-' + RouteUtils.getContext());
        let decodedStorageLanguage = null;
        if (storageLanguage !== null) {
            decodedStorageLanguage = atob(storageLanguage);
            decodedStorageLanguage = JSON.parse(decodedStorageLanguage);
        }
        return decodedStorageLanguage;
    },

    updateUserLanguageLocal(newLanguage: string, newLanguageID: number, i18n: any) {
        const storageUser = localStorage.getItem('user-' + RouteUtils.getContext());
        let decodedStorageUser = null;
        if (storageUser !== null) {
            decodedStorageUser = atob(storageUser);
            decodedStorageUser = JSON.parse(decodedStorageUser);
            // Update values
            decodedStorageUser.language = newLanguage;
            decodedStorageUser.languageId = newLanguageID;
            // Re-encode and store back in local storage
            const updatedUser = JSON.stringify(decodedStorageUser);
            const encodedUser = btoa(updatedUser);
            localStorage.setItem('user-' + RouteUtils.getContext(), encodedUser);
        }
    },

    updateLanguageLocalToken(newLanguage: string, newLanguageID: number) {
        const storageLanguage = localStorage.getItem('language-' + RouteUtils.getContext());
        let decodedStorageLanguage = null;
        if (storageLanguage !== null) {
            decodedStorageLanguage = atob(storageLanguage);
            decodedStorageLanguage = JSON.parse(decodedStorageLanguage);
            // Update values
            decodedStorageLanguage.id = newLanguageID;
            decodedStorageLanguage.name = newLanguage;
            // Re-encode and store back in local storage
            const updatedLanguage = JSON.stringify(decodedStorageLanguage);
            const encodedLanguage = btoa(updatedLanguage);
            localStorage.setItem('language-' + RouteUtils.getContext(), encodedLanguage);
            i18n.changeLanguage(newLanguage);
        }
    },

    //TODO : refactor accountaffiliate and accountsupplier here
    getLocal() {
        const user = this.getUser();
        return user ? user.locale : null;
    },

    getLanguage() {
        const locale = this.getLocal();
        return locale ? locale.split('_')[0] : null;
    },

    getId() {
        const user = this.getUser();
        return user ? user.userID : null;
    },

    isLogged() {
        let accessToken = localStorage.getItem('access_token');
        return !!accessToken;
    },

    refreshToken: async function (): Promise<AuthenticationResponse> {
        if (!ApiRoutes.REACT_APP_URL_REFRESH_TOKEN) {
            throw new Error("REACT_APP_URL_REFRESH_TOKEN is not defined");
        }
        return await requestService.get(ApiRoutes.REACT_APP_URL_REFRESH_TOKEN, {}, {
            Accept: "application/json",
            Authorization: 'Bearer ' + this.getRefreshToken(),
        }).then((response) => {
            if (response.status === 200) {
                this.saveToken(response.data.accessToken);
                return response.data as AuthenticationResponse;
            } else {
                throw new Error("Error during token refresh");
            }
        }).catch(() => {
            throw new Error("Error during token refresh");
        });
    },

    //todo change to react_login url if logged out due to not being auth
    logout: async function (redirect = true) {
        if (!ApiRoutes.REACT_APP_URL_LOGOUT) {
            throw new Error("REACT_APP_URL_LOGOUT is not defined");
        }
        return await requestService.get(ApiRoutes.REACT_APP_URL_LOGOUT, {})
            .then(() => {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem("user-" + RouteUtils.getContext());
                localStorage.setItem("redirect_to", window.location.href);
            })
            .catch((error) => {
                console.error('Error during server logout: ', error);
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem("user-" + RouteUtils.getContext());
                localStorage.setItem("redirect_to", window.location.href);
            }).finally(() => {
                if (redirect) {
                    window.location.href = ApiRoutes.APP_URL_LOGIN ?? "";
                }
            });
    },

    forgotPassword: async function (email: string): Promise<any> {
        return await Axios.post(ApiRoutes.REACT_APP_URL_FORGOT_PASSWORD, {}, {
            params: {
                email: email
            }
        }).then((response) => {
            return response;
        });
    },

    checkIfEmailVerificationTokenIsValid: async function (token: string) {
        return await requestService.get(ApiRoutes.REACT_APP_URL_RESET_PASSWORD_TOKEN + `/${token}`, {}).then((response) => {
            return response;
        });
    },

    /**
     * @deprecated
     */
    getBackofficeCookie: async function () {
        let token = this.getCookie('jwt-token-' + RouteUtils.getContext());
        if (token !== "") {
            this.saveRefreshToken(token);
            return await this.refreshToken().then(r => {
                this.saveToken(r.accessToken);
                this.saveUser(r);
                return r;
            });
        }
        return null;
    },

    getCookie(cname: string) {
        let name = cname + "=";
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    },

    setSidebarState(isOpen: boolean) {
        localStorage.setItem('sidebarState', isOpen ? 'open' : 'closed');
    },

    getSidebarState() {
        return localStorage.getItem('sidebarState') !== 'closed';
    },

    hasAuthority(authorities: string[] | undefined) {
        if (!authorities) {
            return true;
        }

        const user = this.getUser();
        if (user && user.authorities) {
            for (let authority of user.authorities) {
                if (authorities.includes(authority)) {
                    return true;
                }
            }
        }
        return false;
    },

    getAuthorities() {
        const user = this.getUser();
        if (user) {
            return user.authorities;
        }
        return [];
    },

    hasRole(role: string) {
        if (!role) {
            return true;
        }
        const user = this.getUser();
        if (user && user.role) {
            return user.role === role;
        }
        return false;
    },

};